import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { caseStudies, fabricDetails } from "../mock";
import { ArrowLeft, ArrowRight, CheckCircle2, Database, Circle, TrendingUp, TrendingDown } from "lucide-react";

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const cs = caseStudies.find((c) => c.slug === slug);
  const deep = fabricDetails[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!cs) {
    return (
      <div className="pt-40 pb-24 container-x text-center">
        <h1 className="text-3xl font-bold text-[#0B1B3B]">Case study not found</h1>
        <Link to="/insights" className="btn-primary mt-6 inline-flex">← Back to all case studies</Link>
      </div>
    );
  }

  const related = caseStudies.filter((c) => c.slug !== slug).slice(0, 2);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-[120px] pb-16 bg-[#0B1B3B] text-white overflow-hidden">
        <div className="absolute inset-0 grid-lines-bg opacity-40" />
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full orb"
             style={{ background: "radial-gradient(closest-side, rgba(30,86,227,0.28), transparent 70%)" }} />

        <div className="container-x relative">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft size={16} /> Back to case studies
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold tracking-widest text-white bg-[#1E56E3] px-3 py-1 rounded">{cs.tag}</span>
            <span className="text-[11px] uppercase tracking-wider text-slate-400">{cs.industry}</span>
          </div>

          <h1 className="mt-4 text-3xl md:text-5xl font-extrabold leading-tight max-w-4xl">{cs.title}</h1>

          <div className="mt-8 grid md:grid-cols-3 gap-6 max-w-3xl">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Client</div>
              <div className="mt-1 text-[15px] font-medium">{cs.client}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Industry</div>
              <div className="mt-1 text-[15px] font-medium">{cs.industry}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Duration</div>
              <div className="mt-1 text-[15px] font-medium">{cs.duration}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Strip */}
      <section className="py-14 bg-white border-b border-slate-200">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-6">
          {cs.outcomes.map((o) => (
            <div key={o.label} className="text-center reveal">
              <div className="text-4xl md:text-5xl font-extrabold text-[#0B1B3B]">{o.metric}</div>
              <div className="mt-2 text-[12px] uppercase tracking-wider text-slate-500">{o.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Body */}
      <section className="py-20 bg-[#F5F7FB]">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          {/* Sidebar Stack */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 self-start">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2">
                <Database size={16} className="text-[#1E56E3]" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#0B1B3B]">Tech Stack</h3>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {cs.stack.map((s) => (
                  <span key={s} className="text-[12px] font-medium bg-[#EEF3FF] text-[#1E56E3] px-3 py-1.5 rounded-full">{s}</span>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#0B1B3B]">Engagement</h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Client</span><span className="font-semibold text-[#0B1B3B] text-right">{cs.client}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Industry</span><span className="font-semibold text-[#0B1B3B]">{cs.industry}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Duration</span><span className="font-semibold text-[#0B1B3B]">{cs.duration}</span></div>
              </div>
            </div>

            <a href="/contact" className="btn-primary w-full justify-center">Discuss a similar project <ArrowRight size={16} /></a>
            {cs.tag === "MICROSOFT FABRIC" && (
              <Link to="/fabric-playbook" className="btn-dark w-full justify-center mt-3">
                Download Fabric Playbook <ArrowRight size={16} />
              </Link>
            )}
          </aside>

          {/* Main content */}
          <article className="lg:col-span-8 space-y-10">
            <div className="reveal">
              <span className="tag-chip">The Challenge</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0B1B3B]">Where things stood before</h2>
              <p className="mt-4 text-[16px] leading-relaxed text-slate-600">{cs.challenge}</p>
            </div>

            <div className="reveal">
              <span className="tag-chip">Our Approach</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0B1B3B]">How we engineered the outcome</h2>
              <ul className="mt-5 space-y-3">
                {cs.approach.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-[15px] text-slate-700 leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="reveal">
              <span className="tag-chip">The Outcomes</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0B1B3B]">Measurable business impact</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {cs.outcomes.map((o) => (
                  <div key={o.label} className="rounded-2xl border border-slate-200 p-6 bg-gradient-to-br from-white to-[#F5F7FB]">
                    <div className="text-4xl font-extrabold text-[#0B1B3B]">{o.metric}</div>
                    <p className="mt-2 text-sm text-slate-600">{o.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Fabric deep-dive: Architecture + Before/After */}
      {deep && (
        <section className="py-20 bg-white">
          <div className="container-x space-y-12">
            {/* Architecture */}
            <div>
              <div className="max-w-3xl reveal">
                <span className="tag-chip">Architecture Diagram</span>
                <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0B1B3B]">{deep.architecture.title}</h2>
                <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">The reference architecture we deployed for this engagement, layer by layer.</p>
              </div>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F8FAFF] to-white p-6 md:p-8 overflow-x-auto">
                <div className="flex items-stretch gap-4 min-w-[720px]">
                  {deep.architecture.layers.map((layer, li) => (
                    <React.Fragment key={layer.name}>
                      <div className="flex-1 rounded-xl border border-slate-200 bg-white p-5 min-w-[160px]">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full pulse-dot" style={{ background: layer.color }} />
                          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: layer.color }}>{layer.name}</div>
                        </div>
                        <ul className="mt-3 space-y-1.5">
                          {layer.items.map((it) => (
                            <li key={it} className="text-[13px] font-semibold text-[#0B1B3B] leading-snug">{it}</li>
                          ))}
                        </ul>
                      </div>
                      {li < deep.architecture.layers.length - 1 && (
                        <div className="self-center text-slate-400">
                          <ArrowRight size={18} className="float-slow" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Before / After */}
            <div>
              <div className="max-w-3xl reveal">
                <span className="tag-chip">Before / After</span>
                <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#0B1B3B]">The transformation, in numbers</h2>
              </div>
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-slate-200 p-7 bg-[#FDF4F4]">
                  <div className="flex items-center gap-2 text-[#B91C1C]">
                    <TrendingDown size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">{deep.before.title}</span>
                  </div>
                  <div className="mt-5 space-y-3">
                    {deep.before.metrics.map((m) => (
                      <div key={m.k} className="flex items-center justify-between border-b border-red-100 pb-2">
                        <span className="text-sm text-slate-600">{m.k}</span>
                        <span className="text-lg font-extrabold text-[#B91C1C]">{m.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 p-7 bg-[#F0FDF4]">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <TrendingUp size={16} />
                    <span className="text-xs font-bold uppercase tracking-widest">{deep.after.title}</span>
                  </div>
                  <div className="mt-5 space-y-3">
                    {deep.after.metrics.map((m) => (
                      <div key={m.k} className="flex items-center justify-between border-b border-emerald-100 pb-2">
                        <span className="text-sm text-slate-600">{m.k}</span>
                        <span className="text-lg font-extrabold text-emerald-700">{m.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      <section className="py-16 bg-[#F5F7FB]">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1B3B]">Related case studies</h2>
            <Link to="/insights" className="text-sm font-semibold text-[#1E56E3] inline-flex items-center gap-1">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/case-studies/${r.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover-lift block">
                <div className="relative h-48 overflow-hidden">
                  <img src={r.img} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/85 to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest text-white bg-[#1E56E3] px-3 py-1 rounded">{r.tag}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0B1B3B]">{r.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{r.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
